/* Natureza de Operacao (Fiscal Operations) — CRUD page.
 * Centraliza tributacao da emissao de NF-e/NFC-e. Produto carrega apenas
 * NCM/CEST/Origem; o resto eh definido aqui e aplicado conforme operacao.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import {
  Plus, X, Save, Edit, Trash2, FileText, Building2, Search, Share2, Check,
} from 'lucide-react';
import { FiscalCertCard } from '../components/FiscalCertCard';
import { confirmDialog } from '../components/ConfirmDialog';

const ERP = `${API}/erp`;
const inputClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20";

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
// iter156g: UFs com SEFAZ homologado no sistema (emissao real testada).
// SO estas podem ser origem de nota (UF do emitente).
// Para adicionar uma UF: validar webservice SEFAZ + certificado + XML valido, dai incluir aqui.
// iter156i: MT liberado (SEFAZ MT usa portal www.sefaz.mt.gov.br/portal/nfce/).
// iter156o: RO liberado — RO delega NFCe para SVRS (nfce.svrs.rs.gov.br).
const UFS_ORIGEM_HABILITADAS = ['GO', 'MT', 'RO', 'MG'];

const TIPOS = [
  { v: 'saida',   label: 'Saida' },
  { v: 'entrada', label: 'Entrada' },
];
const REGIMES = [
  { v: 'simples',          label: 'Simples Nacional' },
  { v: 'lucro_real',       label: 'Lucro Real' },
  { v: 'lucro_presumido',  label: 'Lucro Presumido' },
];
const PESSOAS = [
  { v: 'fisica',   label: 'Fisica' },
  { v: 'juridica', label: 'Juridica' },
  { v: 'ambos',    label: 'Fisica + Juridica' },
];
const TIPOS_EMISSAO = [
  { v: 'normal',                   label: '1 - Normal' },
  { v: 'contingencia_fsda',        label: '2 - Contingencia FS-DA' },
  { v: 'contingencia_scan',        label: '3 - Contingencia SCAN' },
  { v: 'contingencia_dpec',        label: '4 - Contingencia DPEC' },
  { v: 'contingencia_fsdaepd',     label: '5 - Contingencia FSDA' },
  { v: 'contingencia_svc_an',      label: '6 - Contingencia SVC-AN' },
  { v: 'contingencia_svc_rs',      label: '7 - Contingencia SVC-RS' },
  { v: 'contingencia_offline',     label: '9 - Contingencia Off-Line' },
];
const FINALIDADES = [
  { v: '1', label: '1 - Normal' },
  { v: '2', label: '2 - Complementar' },
  { v: '3', label: '3 - Ajuste' },
  { v: '4', label: '4 - Devolucao' },
];
const IDENT_OPS = [
  { v: '1', label: '1 - Interna' },
  { v: '2', label: '2 - Interestadual' },
  { v: '3', label: '3 - Exterior' },
];
const TIPOS_CONSUMIDOR = [
  { v: '0', label: '0 - Normal' },
  { v: '1', label: '1 - Consumidor Final' },
];
const PRESENCAS = [
  { v: '0', label: '0 - Nao se aplica' },
  { v: '1', label: '1 - Presencial' },
  { v: '2', label: '2 - Internet' },
  { v: '3', label: '3 - Tele-atendimento' },
  { v: '4', label: '4 - NFC-e em entrega a domicilio' },
  { v: '5', label: '5 - Presencial fora do estabelecimento' },
  { v: '9', label: '9 - Outros' },
];

// CST/CSOSN options reduced to most-used
const CST_ICMS = ['00','10','20','30','40','41','50','51','60','70','90','101','102','103','201','202','203','300','400','500','900'];
const CST_PIS_COFINS = ['01','02','03','04','05','06','07','08','09','49','50','51','52','53','54','55','56','60','61','62','63','64','65','66','67','70','71','72','73','74','75','98','99'];
const CST_IPI = ['00','01','02','03','04','05','49','50','51','52','53','54','55','99'];

const emptyForm = () => ({
  description: '',
  tipo: 'saida',
  descricao_nf: '',
  cfop: '',
  regime_tributario: 'simples',
  pessoa: 'ambos',
  tipo_emissao: 'normal',
  finalidade: '1',
  identificacao_operacao: '1',
  tipo_consumidor: '1',
  presenca_comprador: '1',
  icms:   { cst: '', aliquota: '', aliquota_st: '', mva: '', red_bc: '', modalidade_bc: '3', credito_sn_aliquota: '', st_embutido_preco: false, fcp_st_aliquota: '' },
  ipi:    { cst: '', aliquota: '' },
  pis:    { cst: '', aliquota: '' },
  cofins: { cst: '', aliquota: '' },
  regras: { credita_estoque: false, gera_financeiro: false, gera_duplicata: false },  // iter77bb: defaults FALSE — user habilita por natureza.
  estados_origem:  ['*'],
  estados_destino: ['*'],
  info_complementares: '',
  info_fisco: '',
  active: true,
});

const num = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return isNaN(n) ? null : n;
};
const cleanNumbers = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    if (['aliquota','aliquota_st','mva','red_bc','credito_sn_aliquota','fcp_st_aliquota'].includes(k)) {
      const n = num(v);
      if (n != null) out[k] = n;
    } else {
      out[k] = v;
    }
  });
  return out;
};

const SubTabBtn = ({ k, label, subTab, setSubTab }) => (
  <button type="button" onClick={() => setSubTab(k)} data-testid={`natop-subtab-${k}`}
    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${subTab === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-700'}`}>
    {label}
  </button>
);

export const NaturezaOperacaoPage = ({ companyId: activeCompanyId = null, companies = [] } = {}) => {
  const [topTab, setTopTab] = useState('natureza');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [subTab, setSubTab] = useState('cabecalho');
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  // iter156c: modal de compartilhamento de Natureza de Operacao com outras filiais
  const [shareTarget, setShareTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTipo) params.append('tipo', filterTipo);
      // iter156c: escopo por filial ativa (mostra natureza da filial + compartilhadas + legado sem company_id)
      if (activeCompanyId) params.append('company_id', activeCompanyId);
      const { data } = await axios.get(`${ERP}/natureza-operacao?${params}`, { withCredentials: true });
      setItems(data.data || []);
    } catch {
      toast.error('Erro ao carregar Naturezas de Operacao');
    } finally { setLoading(false); }
  }, [filterTipo, activeCompanyId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return items;
    return items.filter(i => (i.description || '').toLowerCase().includes(s) || (i.cfop || '').includes(s) || String(i.code || '').includes(s));
  }, [items, search]);

  const reset = () => { setForm(emptyForm()); setEditing(null); setSubTab('cabecalho'); };
  const openNew = () => { reset(); setShowForm(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({
      ...emptyForm(),
      ...n,
      icms:   { ...emptyForm().icms,   ...(n.icms || {}) },
      ipi:    { ...emptyForm().ipi,    ...(n.ipi || {}) },
      pis:    { ...emptyForm().pis,    ...(n.pis || {}) },
      cofins: { ...emptyForm().cofins, ...(n.cofins || {}) },
      regras: { ...emptyForm().regras, ...(n.regras || {}) },
      estados_origem:  n.estados_origem  || ['*'],
      estados_destino: n.estados_destino || ['*'],
    });
    setSubTab('cabecalho');
    setShowForm(true);
  };

  // iter156g: TODOS agora seleciona explicitamente cada UF (nao um wildcard `*`) — assim
  // o usuario pode desmarcar 1-2 UFs individuais e manter as demais selecionadas.
  // Para Estados de Origem, apenas UFs habilitadas (SEFAZ homologado) sao consideradas.
  const toggleUF = (field, uf) => setForm(p => {
    const cur = p[field] || [];
    if (uf === '*') {
      // clique em TODOS: marca cada UF explicitamente. Ao clicar de novo com todos marcados, limpa.
      const isOrigem = field === 'estados_origem';
      const pool = isOrigem ? UFS_ORIGEM_HABILITADAS : UFS;
      const currentSet = new Set(cur.filter(x => x !== '*'));
      const allSelected = pool.every(u => currentSet.has(u));
      return { ...p, [field]: allSelected ? [] : [...pool] };
    }
    let next = cur.filter(x => x !== '*');
    if (next.includes(uf)) next = next.filter(x => x !== uf); else next = [...next, uf];
    return { ...p, [field]: next };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error('Informe a descricao');
    if (!form.cfop.trim()) return toast.error('Informe o CFOP');
    const payload = {
      ...form,
      icms:   cleanNumbers(form.icms),
      ipi:    cleanNumbers(form.ipi),
      pis:    cleanNumbers(form.pis),
      cofins: cleanNumbers(form.cofins),
    };
    try {
      if (editing) {
        await axios.put(`${ERP}/natureza-operacao/${editing.id}`, payload, { withCredentials: true });
        toast.success('Natureza atualizada');
      } else {
        await axios.post(`${ERP}/natureza-operacao`, payload, { withCredentials: true });
        toast.success('Natureza criada');
      }
      setShowForm(false); reset(); await fetchAll();
    } catch (err) {
      const d = err.response?.data?.detail || err.message || 'Erro';
      toast.error(`Erro ao salvar: ${typeof d === 'string' ? d : JSON.stringify(d)}`);
    }
  };

  const remove = async (n) => {
    if (!(await confirmDialog({ title: `Excluir "${n.description}"? Acao irreversivel.`, confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/natureza-operacao/${n.id}`, { withCredentials: true });
      toast.success('Excluida');
      fetchAll();
    } catch { toast.error('Erro ao excluir'); }
  };

  // === FORM ===
  if (showForm) return (
    <div data-testid="natop-form">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2"><FileText size={22} className="text-violet-600" /> {editing ? `Editar #${editing.code}` : 'Nova Natureza de Operacao'}</h2>
        <button onClick={() => { setShowForm(false); reset(); }} className="text-slate-500 hover:text-slate-700" data-testid="natop-close"><X size={20} /></button>
      </div>
      <form onSubmit={submit} className="bg-white border-2 border-violet-200 rounded-2xl">
        {/* Sub-tabs */}
        <div className="border-b border-slate-200 px-4 flex gap-1 overflow-x-auto">
          <SubTabBtn k="cabecalho" label="Cabecalho" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="icms"      label="ICMS" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="ipi"       label="IPI" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="pis"       label="PIS" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="cofins"    label="COFINS" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="regras"    label="Regras" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="estados"   label="Estados" subTab={subTab} setSubTab={setSubTab} />
          <SubTabBtn k="info"      label="Info NF" subTab={subTab} setSubTab={setSubTab} />
        </div>

        <div className="p-5">

        {subTab === 'cabecalho' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="natop-cab">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">Descricao *</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="Ex: Venda de Mercadoria" required data-testid="natop-description" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Tipo *</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className={inputClass} data-testid="natop-tipo">
                {TIPOS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-bold text-slate-600 mb-1">DescricaoNF (sai impresso na nota)</label>
              <input value={form.descricao_nf} onChange={e => setForm({ ...form, descricao_nf: e.target.value })} className={inputClass} placeholder="Texto que aparece no campo Natureza da NF-e" data-testid="natop-descnf" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">CFOP *</label>
              <input value={form.cfop} onChange={e => setForm({ ...form, cfop: e.target.value.replace(/[^0-9]/g,'').slice(0,4) })} className={inputClass} placeholder="5102" required data-testid="natop-cfop" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Regime Tributario</label>
              <select value={form.regime_tributario} onChange={e => setForm({ ...form, regime_tributario: e.target.value })} className={inputClass} data-testid="natop-regime">
                {REGIMES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Pessoa</label>
              <select value={form.pessoa} onChange={e => setForm({ ...form, pessoa: e.target.value })} className={inputClass} data-testid="natop-pessoa">
                {PESSOAS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Tipo Emissao</label>
              <select value={form.tipo_emissao} onChange={e => setForm({ ...form, tipo_emissao: e.target.value })} className={inputClass} data-testid="natop-emissao">
                {TIPOS_EMISSAO.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Finalidade</label>
              <select value={form.finalidade} onChange={e => setForm({ ...form, finalidade: e.target.value })} className={inputClass} data-testid="natop-finalidade">
                {FINALIDADES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Identificacao Operacao</label>
              <select value={form.identificacao_operacao} onChange={e => setForm({ ...form, identificacao_operacao: e.target.value })} className={inputClass} data-testid="natop-identop">
                {IDENT_OPS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Tipo Consumidor</label>
              <select value={form.tipo_consumidor} onChange={e => setForm({ ...form, tipo_consumidor: e.target.value })} className={inputClass} data-testid="natop-consumidor">
                {TIPOS_CONSUMIDOR.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Presenca do Comprador</label>
              <select value={form.presenca_comprador} onChange={e => setForm({ ...form, presenca_comprador: e.target.value })} className={inputClass} data-testid="natop-presenca">
                {PRESENCAS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {subTab === 'icms' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="natop-icms">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">CST / CSOSN</label>
              <select value={form.icms.cst} onChange={e => setForm({ ...form, icms: { ...form.icms, cst: e.target.value } })} className={inputClass} data-testid="natop-icms-cst">
                <option value="">—</option>
                {CST_ICMS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="text-[10px] text-slate-400 mt-0.5">Regime Normal: CST (2 dig) · Simples: CSOSN (3 dig)</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Aliquota (%)</label>
              <input type="number" step="0.01" value={form.icms.aliquota} onChange={e => setForm({ ...form, icms: { ...form.icms, aliquota: e.target.value } })} className={inputClass} placeholder="18.00" data-testid="natop-icms-aliq" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Aliq. ST (%)</label>
              <input type="number" step="0.01" value={form.icms.aliquota_st} onChange={e => setForm({ ...form, icms: { ...form.icms, aliquota_st: e.target.value } })} className={inputClass} placeholder="0.00" data-testid="natop-icms-aliq-st" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">MVA (%)</label>
              <input type="number" step="0.01" value={form.icms.mva} onChange={e => setForm({ ...form, icms: { ...form.icms, mva: e.target.value } })} className={inputClass} placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Reducao Base (%)</label>
              <input type="number" step="0.01" value={form.icms.red_bc} onChange={e => setForm({ ...form, icms: { ...form.icms, red_bc: e.target.value } })} className={inputClass} placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Modalidade BC</label>
              <select value={form.icms.modalidade_bc} onChange={e => setForm({ ...form, icms: { ...form.icms, modalidade_bc: e.target.value } })} className={inputClass}>
                <option value="0">0 - Margem Valor Agreg.</option>
                <option value="1">1 - Pauta (Valor)</option>
                <option value="2">2 - Preco Tabelado</option>
                <option value="3">3 - Valor da Operacao</option>
              </select>
            </div>
            {/* iter156eg: Simples Nacional — credito SN e ST embutido no preco */}
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">FCP-ST / Fundo de Pobreza (%)</label>
              <input type="number" step="0.01" value={form.icms.fcp_st_aliquota ?? ''} onChange={e => setForm({ ...form, icms: { ...form.icms, fcp_st_aliquota: e.target.value } })} className={inputClass} placeholder="ex: 2.00 (GO PROTEGE)" data-testid="natop-icms-fcp-st" />
              <p className="text-[10px] text-slate-500 mt-1">Vai separado no XML (vBCFCPST/pFCPST/vFCPST). Ex.: GO = Aliq. ST 19% + FCP-ST 2% (nao some os 2% na Aliq. ST).</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Aliq. credito SN — pCredSN (%)</label>
              <input type="number" step="0.01" value={form.icms.credito_sn_aliquota ?? ''} onChange={e => setForm({ ...form, icms: { ...form.icms, credito_sn_aliquota: e.target.value } })} className={inputClass} placeholder="ex: 1.25 a 3.95" data-testid="natop-icms-cred-sn" />
              <p className="text-[10px] text-slate-500 mt-1">CSOSN 101/201: aliquota <strong>efetiva do Simples</strong> que o comprador pode creditar (art. 23 LC 123). Vazio = usa a "Aliquota (%)" acima (legado).</p>
            </div>
            <label className="md:col-span-2 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!form.icms.st_embutido_preco} onChange={e => setForm({ ...form, icms: { ...form.icms, st_embutido_preco: e.target.checked } })} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid="natop-icms-st-embutido" />
              <div>
                <span className="text-sm font-bold text-slate-800">Preco de venda ja inclui ICMS-ST (embutir)</span>
                <p className="text-[11px] text-slate-600">Quando ligado (CSOSN 201/202/203/900 com Aliq. ST), o valor cobrado do cliente e tratado como <strong>total da nota</strong>: o valor dos produtos fica cheio e o ICMS-ST + FCP-ST entram como <em>desconto</em>, de modo que <em>produtos − desconto + ST + FCP-ST = valor da venda</em>. Ex.: venda R$ 750 (MVA 70, ICMS 19, ST 19, FCP 2) → BC ST 1.275,00 · ST 129,13 · FCP 25,50 · desconto 154,63 · total 750,00. A base do ST e calculada sobre o valor BRUTO (sem desconto). Desligado: o ST e somado por fora (cliente paga preco + ST).</p>
              </div>
            </label>
          </div>
        )}

        {['ipi','pis','cofins'].includes(subTab) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`natop-${subTab}`}>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">CST {subTab.toUpperCase()}</label>
              <select value={form[subTab].cst} onChange={e => setForm({ ...form, [subTab]: { ...form[subTab], cst: e.target.value } })} className={inputClass} data-testid={`natop-${subTab}-cst`}>
                <option value="">—</option>
                {(subTab === 'ipi' ? CST_IPI : CST_PIS_COFINS).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Aliquota (%)</label>
              <input type="number" step="0.01" value={form[subTab].aliquota} onChange={e => setForm({ ...form, [subTab]: { ...form[subTab], aliquota: e.target.value } })} className={inputClass} placeholder="0.00" data-testid={`natop-${subTab}-aliq`} />
            </div>
          </div>
        )}

        {subTab === 'regras' && (
          <div className="space-y-2" data-testid="natop-regras">
            {[
              { k: 'credita_estoque', label: 'Credita Estoque (movimenta estoque)' },
              { k: 'gera_financeiro', label: 'Gera lancamento Financeiro' },
              { k: 'gera_duplicata',  label: 'Gera Duplicata / Conta a Receber' },
            ].map(r => (
              <label key={r.k} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 cursor-pointer">
                <input type="checkbox" checked={!!form.regras?.[r.k]} onChange={e => setForm({ ...form, regras: { ...(form.regras || {}), [r.k]: e.target.checked } })} className="w-4 h-4 accent-violet-600" data-testid={`natop-regra-${r.k}`} />
                <span className="text-sm font-semibold text-slate-700">{r.label}</span>
              </label>
            ))}
          </div>
        )}

        {subTab === 'estados' && (
          <div className="space-y-4" data-testid="natop-estados">
            {[
              { f: 'estados_origem',  label: 'Estados de Origem (UF do emitente)', isOrigem: true },
              { f: 'estados_destino', label: 'Estados de Destino (UF do destinatario)', isOrigem: false },
            ].map(g => {
              const arr = form[g.f] || [];
              const legacyAll = arr.includes('*');
              // iter156g: pool de UFs disponiveis (origem restringe a SEFAZ homologado)
              const pool = g.isOrigem ? UFS_ORIGEM_HABILITADAS : UFS;
              // "todos" = todas do pool explicitamente marcadas (novo modelo) OU legacy '*'
              const effectiveSet = new Set(arr.filter(x => x !== '*'));
              const allSelected = legacyAll || pool.every(u => effectiveSet.has(u));
              return (
                <div key={g.f}>
                  <p className="text-xs font-bold text-slate-700 mb-2">
                    {g.label} <span className="font-normal text-slate-400">({allSelected ? 'TODOS' : `${effectiveSet.size} UFs`})</span>
                  </p>
                  {g.isOrigem && (
                    <p className="text-[11px] text-amber-700 mb-2 leading-relaxed">
                      <strong>Apenas UFs com SEFAZ homologado no sistema estao habilitadas.</strong> Demais UFs
                      ficam desabilitadas ate que a emissao seja testada e liberada para producao.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => toggleUF(g.f, '*')}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-colors ${allSelected ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}
                      data-testid={`natop-uf-${g.f}-all`}>
                      TODOS
                    </button>
                    {UFS.map(uf => {
                      const on = effectiveSet.has(uf) || legacyAll;
                      const enabled = !g.isOrigem || UFS_ORIGEM_HABILITADAS.includes(uf);
                      const title = enabled ? '' : 'SEFAZ desta UF ainda nao homologada no sistema. Solicite ao suporte.';
                      return (
                        <button key={uf} type="button" onClick={() => enabled && toggleUF(g.f, uf)} disabled={!enabled}
                          title={title}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}>
                          {uf}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {subTab === 'info' && (
          <div className="space-y-3" data-testid="natop-info">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Informacoes Complementares (NF)</label>
              <textarea value={form.info_complementares} onChange={e => setForm({ ...form, info_complementares: e.target.value })} className={inputClass} rows={3} placeholder="Texto livre que aparece em Dados Adicionais da NF-e" data-testid="natop-info-comp" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Informacoes para o Fisco</label>
              <textarea value={form.info_fisco} onChange={e => setForm({ ...form, info_fisco: e.target.value })} className={inputClass} rows={2} placeholder="Texto destinado ao fisco" data-testid="natop-info-fisco" />
            </div>
          </div>
        )}

        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <label className="flex items-center gap-2 mr-auto">
            <input type="checkbox" checked={form.active !== false} onChange={e => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="natop-active" />
            <span className="text-xs font-bold text-slate-700">Ativa</span>
          </label>
          <button type="button" onClick={() => { setShowForm(false); reset(); }} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">Cancelar</button>
          <button type="submit" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold" data-testid="natop-save"><Save size={14} /> {editing ? 'Atualizar' : 'Salvar'}</button>
        </div>
      </form>
    </div>
  );

  // === LIST ===
  return (
    <div data-testid="natop-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2"><FileText size={22} className="text-violet-600" /> Config. de NF</h2>
          <p className="text-xs text-slate-500 mt-0.5">Centraliza as configuracoes para emissao de notas fiscais (NF-e / NFC-e).</p>
        </div>
      </div>

      {/* Top tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-0 overflow-x-auto">
        {[
          { k: 'nfe',          label: 'NF-e' },
          { k: 'nfce',         label: 'NFC-e' },
          { k: 'natureza',     label: 'Natureza de Operacao' },
          { k: 'inutilizacao', label: 'Inutilizacao' },
          { k: 'certificado',  label: 'Certificado A1' },
        ].map(t => (
          <button key={t.k} type="button" onClick={() => setTopTab(t.k)} data-testid={`confignf-tab-${t.k}`}
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${topTab === t.k ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {topTab === 'certificado' && <FiscalCertCard companyId={activeCompanyId} companies={companies} />}
      {(topTab === 'nfe' || topTab === 'nfce') && (
        <div className="mb-4 p-3 rounded-2xl border-2 border-cyan-200 bg-cyan-50 flex items-start gap-3" data-testid="fiscal-active-company-banner">
          <div className="text-xs text-cyan-900">
            <p className="font-black uppercase tracking-wider text-[10px] mb-0.5">Configuracao fiscal desta filial</p>
            <p>Numeracao NF-e/NFC-e (ultimo numero emitido) e CSC/Token sao <strong>unicos por CNPJ</strong>. Voce esta editando a configuracao da <strong>filial ativa</strong> selecionada no topo. Para configurar outra filial, troque no seletor do menu superior.</p>
          </div>
        </div>
      )}
      {topTab === 'nfe' && <NFConfigForm modelo="55" testidPrefix="nfe" companyId={activeCompanyId || ''} />}
      {topTab === 'nfce' && <NFConfigForm modelo="65" testidPrefix="nfce" companyId={activeCompanyId || ''} />}
      {topTab === 'inutilizacao' && <PlaceholderTab title="Inutilizacao de Numeracao" hint="Quando ha 'pulos' na numeracao das notas, inutilize o intervalo na SEFAZ pra nao ter problema na fiscalizacao." />}
      {topTab !== 'natureza' && topTab !== 'certificado' && null}

      {topTab === 'natureza' && (
      <>
      <div className="flex items-center justify-end mb-3">
        <button onClick={openNew} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold" data-testid="add-natop"><Plus size={16} /> Nova Natureza</button>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por descricao, CFOP ou codigo..." className={`${inputClass} pl-8`} data-testid="natop-search" />
        </div>
        <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)} className={`${inputClass} w-auto`} data-testid="natop-filter-tipo">
          <option value="">Todos os tipos</option>
          {TIPOS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-2 border-violet-600 border-t-transparent" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-slate-200">
          <Building2 size={36} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 font-bold text-sm">Nenhuma natureza cadastrada</p>
          <p className="text-slate-400 text-xs mt-1">Crie uma natureza para emitir NF-e/NFC-e.</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden" data-testid="natop-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Descricao</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">CFOP</th>
                <th className="px-3 py-2">Regime</th>
                <th className="px-3 py-2">CST ICMS</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(n => {
                const nShared = (n.shared_company_ids || []).length;
                const isOwn = !!activeCompanyId && n.company_id === activeCompanyId;
                return (
                <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`natop-row-${n.id}`}>
                  <td className="px-3 py-2 text-slate-500 font-mono text-xs">{n.code}</td>
                  <td className="px-3 py-2 font-bold text-slate-800">
                    <span className="flex items-center gap-2">
                      {n.description}
                      {nShared > 0 && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cyan-100 text-cyan-800 border border-cyan-300 px-1.5 py-0.5 rounded-full flex-shrink-0" title={`Compartilhada com ${nShared} filial(is)`}>
                          <Share2 size={9} />{nShared}
                        </span>
                      )}
                      {!n.company_id && (
                        <span className="text-[9px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-300 px-1.5 py-0.5 rounded-full flex-shrink-0" title="Legado / global">Global</span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${n.tipo === 'saida' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{n.tipo.toUpperCase()}</span></td>
                  <td className="px-3 py-2 font-mono">{n.cfop}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{REGIMES.find(r => r.v === n.regime_tributario)?.label || n.regime_tributario}</td>
                  <td className="px-3 py-2 font-mono text-xs">{n.icms?.cst || '—'}</td>
                  <td className="px-3 py-2">{n.active === false ? <span className="text-[10px] text-slate-400 font-bold">INATIVA</span> : <span className="text-[10px] text-emerald-700 font-bold">ATIVA</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setShareTarget(n)} className="p-1.5 text-slate-400 hover:text-cyan-600" title={isOwn ? 'Compartilhar com filiais' : 'Ver compartilhamento'} data-testid={`natop-share-${n.id}`}><Share2 size={14} /></button>
                    <button onClick={() => openEdit(n)} className="p-1.5 text-slate-400 hover:text-violet-600" title="Editar" data-testid={`natop-edit-${n.id}`}><Edit size={14} /></button>
                    <button onClick={() => remove(n)} className="p-1.5 text-slate-400 hover:text-rose-500" title="Excluir" data-testid={`natop-delete-${n.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
      {shareTarget && (
        <NatOpShareModal
          natop={shareTarget}
          companies={companies}
          onClose={() => setShareTarget(null)}
          onSaved={() => { setShareTarget(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

/* iter156c: Modal de compartilhamento de Natureza de Operacao com filiais.
   Padrao consistente com share de produtos/etiquetas. */
const NatOpShareModal = ({ natop, companies = [], onClose, onSaved }) => {
  const initSelected = useMemo(() => {
    const s = new Set(natop.shared_company_ids || []);
    return s;
  }, [natop.shared_company_ids]);
  const [selected, setSelected] = useState(initSelected);
  const [saving, setSaving] = useState(false);
  const activeCompanies = companies.filter(c => c.active !== false && c.id !== natop.company_id);
  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const markAll = () => setSelected(new Set(activeCompanies.map(c => c.id)));
  const clearAll = () => setSelected(new Set());
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${ERP}/natureza-operacao/${natop.id}/share`, { company_ids: Array.from(selected) }, { withCredentials: true });
      toast.success('Compartilhamento atualizado');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao compartilhar');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="natop-share-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-violet-50">
          <div>
            <h2 className="font-black text-slate-900 text-base flex items-center gap-2"><Share2 size={16} className="text-cyan-600" /> Compartilhar Natureza</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">{natop.description} · CFOP {natop.cfop}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="natop-share-close"><X size={18} /></button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          <p className="text-xs text-slate-600 mb-3">Marque as filiais que <strong>tambem</strong> poderao usar esta Natureza de Operacao. Sem marcar nenhuma, ela fica visivel <strong>apenas na filial que a criou</strong>.</p>
          <div className="flex gap-2 mb-3">
            <button type="button" onClick={markAll} className="text-[11px] font-bold text-violet-700 hover:text-violet-900" data-testid="natop-share-all">Marcar todas</button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={clearAll} className="text-[11px] font-bold text-slate-500 hover:text-slate-700" data-testid="natop-share-none">Desmarcar todas</button>
          </div>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {activeCompanies.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">Nenhuma outra filial disponivel.</p>}
            {activeCompanies.map(c => {
              const checked = selected.has(c.id);
              return (
                <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-cyan-50 border-cyan-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`} data-testid={`natop-share-company-${c.id}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} className="w-4 h-4 accent-cyan-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{c.code} · {c.name}{c.is_matriz && <span className="ml-2 text-[9px] font-black bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">MATRIZ</span>}</p>
                    <p className="text-[11px] text-slate-500 truncate">{c.cnpj || 'Sem CNPJ'}</p>
                  </div>
                  {checked && <Check size={16} className="text-cyan-600" />}
                </label>
              );
            })}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <span className="text-[11px] text-slate-500 mr-auto">{selected.size} filial(is) selecionada(s)</span>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700" data-testid="natop-share-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold disabled:opacity-50" data-testid="natop-share-save">{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
};

/* Sub-component: redirects user to the existing Fiscal cert card in CompanySettings.
   Renders the full card inline here for convenience. */
const CertTabRedirect = () => (
  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-4 py-3 text-sm text-amber-900 mb-4" data-testid="cert-tab-redirect">
    <strong>Certificado Digital A1.</strong> Configure o certificado em <strong>Configuracoes &gt; Fiscal (NF-e)</strong> (no menu superior, icone de engrenagem). Em breve traremos a configuracao direto aqui.
  </div>
);

/* Configuracao especifica de NF-e (modelo 55) ou NFC-e (modelo 65).
   Salva em erp_company_settings sob a chave fiscal_{nfe|nfce}_config. */
const NFConfigForm = ({ modelo, testidPrefix, companyId = '' }) => {
  const [form, setForm] = useState({
    ambiente: '1',           // iter77bb: 1=Producao default (apos validacao em homologacao). 2=Homologacao.
    serie: modelo === '55' ? '1' : '1',
    // iter77bh: rastreamento do ULTIMO numero emitido por ambiente. Proximo emitido = max(db_last, este_valor) + 1.
    // Atualizado automaticamente pelo backend apos cada autorizacao SEFAZ.
    ultimo_numero_producao: 0,
    ultimo_numero_homologacao: 0,
    // legacy: kept for backward compat (read only)
    proximo_numero: 1,
    csc_id: '',              // NFC-e somente
    csc_token: '',           // NFC-e somente
    contingencia_ativa: false,
    versao_layout: '4.00',
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const settingsKey = modelo === '55' ? 'fiscal_nfe_config' : 'fiscal_nfce_config';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        // iter156a: query per-company_id — numeros/CSC sao unicos por CNPJ
        const url = companyId
          ? `${API}/erp/company-settings?company_id=${encodeURIComponent(companyId)}`
          : `${API}/erp/company-settings`;
        const { data } = await axios.get(url, { withCredentials: true });
        if (alive) {
          // reset form on company change — nao vaza dados de uma filial para outra
          const fresh = data?.[settingsKey] || {};
          setForm({
            ambiente: '1',
            serie: modelo === '55' ? '1' : '1',
            ultimo_numero_producao: 0,
            ultimo_numero_homologacao: 0,
            proximo_numero: 1,
            csc_id: '',
            csc_token: '',
            contingencia_ativa: false,
            versao_layout: '4.00',
            ...fresh,
          });
        }
      } catch { /* keep defaults */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [settingsKey, companyId, modelo]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // iter156a: grava per-company_id — nunca cross-CNPJ
      const url = companyId
        ? `${API}/erp/company-settings?company_id=${encodeURIComponent(companyId)}`
        : `${API}/erp/company-settings`;
      await axios.put(url, { [settingsKey]: form }, { withCredentials: true });
      toast.success(`Configuracoes ${modelo === '55' ? 'NF-e' : 'NFC-e'} salvas${companyId ? ' para esta filial' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="py-8 text-center text-xs text-slate-400">Carregando...</div>;

  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid={`${testidPrefix}-config-form`}>
      <h3 className="font-black text-slate-900 flex items-center gap-2"><FileText size={16} className="text-violet-600" /> Configuracoes {modelo === '55' ? 'NF-e (Modelo 55)' : 'NFC-e (Modelo 65)'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={inputClass.includes('w-full') ? "block text-xs font-bold text-slate-600 mb-1" : ""}>Ambiente</label>
          <select value={form.ambiente} onChange={e => setForm({ ...form, ambiente: e.target.value })} className={inputClass} data-testid={`${testidPrefix}-ambiente`}>
            <option value="1">1 - Producao</option>
            <option value="2">2 - Homologacao (testes)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">Serie</label>
          <input value={form.serie} onChange={e => setForm({ ...form, serie: e.target.value.replace(/[^0-9]/g,'').slice(0,3) })} className={inputClass} placeholder="1" inputMode="numeric" data-testid={`${testidPrefix}-serie`} />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">Versao Layout</label>
          <select value={form.versao_layout} onChange={e => setForm({ ...form, versao_layout: e.target.value })} className={inputClass}>
            <option value="4.00">4.00 (atual)</option>
          </select>
        </div>
      </div>
      {/* iter77bh: ULTIMO numero emitido por ambiente (auto-atualizado pelo backend apos cada autorizacao SEFAZ) */}
      <div className="bg-violet-50/40 border border-violet-200 rounded-xl p-3 space-y-3">
        <div>
          <p className="text-xs font-black text-violet-700 uppercase tracking-wider">Numeracao por Ambiente</p>
          <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
            Informe o <strong>ultimo numero ja emitido</strong> em cada ambiente para evitar duplicidade SEFAZ
            (cStat 539). Util para empresas que ja emitiam em outro sistema. O sistema atualiza estes valores
            automaticamente a cada nova autorizacao.
          </p>
          <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
            <strong>Importante:</strong> a SEFAZ nao oferece API publica para listar notas emitidas por CNPJ.
            Se o CNPJ ja emitiu notas em outro sistema, informe manualmente o ultimo numero (consulte seu
            contador ou o sistema anterior) para evitar rejeicoes de duplicidade.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-emerald-700 mb-1">Ultimo numero emitido em PRODUCAO</label>
            <input
              type="number"
              min="0"
              value={form.ultimo_numero_producao || 0}
              onChange={e => setForm({ ...form, ultimo_numero_producao: parseInt(e.target.value || 0, 10) })}
              className={inputClass}
              data-testid={`${testidPrefix}-ultimo-numero-producao`}
              placeholder="0"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">Proxima emissao em producao usara {Math.max(parseInt(form.ultimo_numero_producao || 0, 10), 0) + 1}.</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-amber-700 mb-1">Ultimo numero emitido em HOMOLOGACAO</label>
            <input
              type="number"
              min="0"
              value={form.ultimo_numero_homologacao || 0}
              onChange={e => setForm({ ...form, ultimo_numero_homologacao: parseInt(e.target.value || 0, 10) })}
              className={inputClass}
              data-testid={`${testidPrefix}-ultimo-numero-homologacao`}
              placeholder="0"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">Proxima emissao em homologacao usara {Math.max(parseInt(form.ultimo_numero_homologacao || 0, 10), 0) + 1}.</p>
          </div>
        </div>
      </div>
      {modelo === '65' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">CSC ID (idToken)</label>
            <input value={form.csc_id} onChange={e => setForm({ ...form, csc_id: e.target.value })} className={inputClass} placeholder="000001" data-testid={`${testidPrefix}-csc-id`} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">CSC Token</label>
            <input value={form.csc_token} onChange={e => setForm({ ...form, csc_token: e.target.value })} className={inputClass} placeholder="Codigo de Seguranca do Contribuinte" data-testid={`${testidPrefix}-csc-token`} />
            <p className="text-[10px] text-slate-400 mt-0.5">Obtido no portal da SEFAZ do seu estado. Obrigatorio para NFC-e.</p>
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!form.contingencia_ativa} onChange={e => setForm({ ...form, contingencia_ativa: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid={`${testidPrefix}-contingencia`} />
        <span className="text-xs font-bold text-slate-700">Modo Contingencia (emite offline e transmite depois)</span>
      </label>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold disabled:opacity-50" data-testid={`${testidPrefix}-save`}>
          <Save size={14} /> {busy ? 'Salvando...' : 'Salvar Configuracoes'}
        </button>
      </div>
    </form>
  );
};

const PlaceholderTab = ({ title, hint }) => (
  <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center" data-testid="confignf-placeholder">
    <FileText size={40} className="mx-auto text-slate-300 mb-3" />
    <h3 className="font-black text-slate-700 mb-1">{title}</h3>
    <p className="text-xs text-slate-500 max-w-xl mx-auto">{hint}</p>
    <p className="text-[11px] text-violet-600 font-bold mt-3">Esta configuracao ficara disponivel na proxima etapa da integracao SEFAZ.</p>
  </div>
);

export default NaturezaOperacaoPage;
